import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import { StandingAuthorizationsTableActions } from '../../../create-request/actions';
import { cancelModalContinueClicked, hideCancelModal } from '../../actions/shared.actions';
import { AdobeConstants } from '../../adobe-tagging/AdobeConstants';
import { AdobeHelpers } from '../../adobe-tagging/adobeHelper';
import {
  selectCancelModalActive,
  selectCancelModalContent,
  selectCancelModalGrayButton,
  selectCancelModalGreenButton,
  selectCancelModalIsL1Navigation,
  selectCancelModalNextUrl,
  selectCancelModalTitle,
  selectPreviousContextDifferentToCurrent,
  selectPreviousCreateTypeInContext,
  selectViewInContext,
} from '../../reducers';
import { CreateTypes, navRouteConstants } from '../../utilities/Constants';
import { selectAchRecurringFrequency, selectStagedTransactionType, selectTransactionType } from '../../../create-request/reducers';
import { SharedActions } from '../../actions';
import { ContextService } from 'projects/integration/src/app/context.service';
import { CoreService } from '@charlesschwab/sac-ui-core';

@Component({
  standalone: false,
  selector: 'mm-cancel-modal-v2',
  templateUrl: './cancel-modal-v2.component.html',
  styleUrls: ['./cancel-modal-v2.component.scss'],
})
export class CancelModalV2Component implements OnInit, OnDestroy {
  title = 'Cancel work in progress';
  textBody: string[] = [
    'Canceling will reset your inputs. Are you sure you want to continue?',
  ];
  greenButton: string = "Continue";
  grayButton: string = "Cancel";
  destroy$: Subject<boolean> = new Subject<boolean>();
  modal: EverestModal;
  step$: Observable<string> = new Observable<string>();
  step: string;
  continueClicked: boolean = false;
  navigateTargetOnContinue: string = '';
  isL1Navigation: boolean | undefined;
  currentContextDiffersToPrevious: boolean;
  previousContext: CreateTypes;
  isOneTime: boolean;

  constructor(private store: Store, private router: Router, private contextService: ContextService, private coreService: CoreService) {}

  ngOnInit(): void {
    this.step$ = this.store.select(selectViewInContext);
    this.step$.pipe(takeUntil(this.destroy$)).subscribe((step) => {
      this.step = step;
    });

    this.store
      .select(selectCancelModalActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe((show) => {
        this.modal = document.getElementById('cancel-modal-v2') as EverestModal;
        this.continueClicked = false;
        if (show) this.modal.showModal();
      });

    this.store
      .select(selectCancelModalNextUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe((nextUrl) => (this.navigateTargetOnContinue = nextUrl));

    this.store
      .select(selectCancelModalIsL1Navigation)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isL1Nav) => this.isL1Navigation = isL1Nav);

    this.store
      .select(selectCancelModalContent)
      .pipe(
        takeUntil(this.destroy$),
        filter((content) => content ? content.length > 0 : false)
      )
      .subscribe((content) => {
        if (content) {
          this.textBody = content;
        }
      });

      this.store
        .select(selectPreviousContextDifferentToCurrent)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (currentContextDiffersToPrevious) =>
            (this.currentContextDiffersToPrevious =
              currentContextDiffersToPrevious)
        );

      this.store
        .select(selectPreviousCreateTypeInContext)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (previousContext) => (this.previousContext = previousContext)
        );

      this.store
        .select(selectAchRecurringFrequency)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (recurringFrequency) => (this.isOneTime = !recurringFrequency)
        );


    this.store
      .select(selectCancelModalTitle)
      .pipe(
        takeUntil(this.destroy$),
        filter((title) => !!title)
      )
      .subscribe((title) => {
        if (title) {
          this.title = title;
        }
      });

    this.store
      .select(selectCancelModalGreenButton)
      .pipe(
        takeUntil(this.destroy$),
        filter((greenButton) => !!greenButton)
      )
      .subscribe((greenButton) => {
        if (greenButton) {
          this.greenButton = greenButton;
        }
      });

    this.store
      .select(selectCancelModalGrayButton)
      .pipe(
        takeUntil(this.destroy$),
        filter((grayButton) => !!grayButton)
      )
      .subscribe((grayButton) => {
        if (grayButton) {
          this.grayButton = grayButton;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onCancelClick(): void {
    this.modal.hideModal();
  }

  onContinueClick(): void {
    // commenting this out for now since we'll need to determine what we want to track via adobe on create standing auth
    // this.adobeTagPageSelector();
    this.continueClicked = true;
    const currentUrl: string = window.location.href;
    this.modal.hideModal();
    if (this.isL1Navigation) {
      window.location.href = `${window.location.origin}${this.navigateTargetOnContinue}`;
    } 
    else if (this.shouldReturnToPreviousContext && currentUrl.includes(this.navigateTargetOnContinue)) {
      if (this.contextService.isMonolithUrl(currentUrl)) { 
        this.router.navigate([this.navRouteString])
      }
      else
      {
        let encodedContext = decodeURIComponent(this.coreService.currentContext.encodedContext as string);
        this.router.navigate([this.navRouteString], { queryParams: { ec: encodedContext } });
      }
      this.store.dispatch(SharedActions.previousContextNavigation())
    } 
    else if (this.navigateTargetOnContinue !== '') {
      if (this.contextService.isMonolithUrl(currentUrl)) { 
        this.router.navigate([this.navigateTargetOnContinue])
      }
      else
      {
        let encodedContext = decodeURIComponent(this.coreService.currentContext.encodedContext as string);
        if(this.navigateTargetOnContinue.includes('?'))
        {
          this.router.navigate([this.navigateTargetOnContinue.split('?')[0]], { queryParams: { ec: encodedContext } });
        }
        else
        {
          this.router.navigate([this.navigateTargetOnContinue], { queryParams: { ec: encodedContext } });
        }
      }
    }
  }

  onModalClose(event: any) {
    if (this.continueClicked) {
      this.store.dispatch(cancelModalContinueClicked());
      // reset continueClicked in the store so that navbar cancel popup still shows up after clicking cancel on the form
      this.store.dispatch(hideCancelModal());
    } else {
      this.store.dispatch(
        StandingAuthorizationsTableActions.startTransacionFlowComplete()
      );
      this.store.dispatch(hideCancelModal());
    }
  }

  get navRouteString(): string {
    switch(this.previousContext) {
      case('standing-authorization'): return navRouteConstants.standingAuth;
      case('transaction'): return navRouteConstants.createTransaction;
      default: return navRouteConstants.createTransaction;
    }
  }

  get shouldReturnToPreviousContext(): boolean {
    return this.currentContextDiffersToPrevious;
  }

  // Tagging must be done done differently on the input step verse the review step to ensure both are recorded
//   adobeTagPageSelector(): void {
//     if (this.step === 'input') {
//       AdobeHelpers.fireClick(
//         AdobeConstants.ADOBE_ +
//           AdobeConstants._CANCEL +
//           AdobeConstants._CONTINUE
//       );
//     } else if (this.step === 'review') {
//       AdobeHelpers.fireClick(
//         AdobeConstants.LINKID +
//           AdobeConstants._CANCEL +
//           AdobeConstants._CONTINUE
//       );
//     }
//   }
// }
}

type EverestModal = HTMLElement & {
  showModal: () => {};
  hideModal: () => {};
};
